// FinancialsPage — office-wide Financial Dashboard (owner/admin/back_office).
//
// Six tables backed by financials_annual + financials_monthly (JSONB).
// KPI list lives in @gutterinv/shared/financials. Inline-edit per cell.
// Variance tint: green when better than target, pink/red when worse —
// "better" is normalized via signedVariance() in the shared module so
// lower-better KPIs (Owner Hours, Callback Rate, etc.) flip correctly.

import { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TrendingUp, DollarSign, Percent, Package,
  type LucideIcon,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip, Legend,
} from 'recharts';
import {
  ANNUAL_KPIS,
  MONTHLY_KPIS,
  signedVariance,
  type KpiDef,
  type FinancialKpiDirection,
  type FinancialKpiUnit,
} from '@gutterinv/shared';
import {
  getFinancials,
  putFinancialsAnnual,
  putFinancialsMonthly,
  type FinancialsResponseClient,
} from '../api/client.js';
import { qk } from '../lib/queryKeys.js';
import {
  formatCentsRounded,
  formatCentsK,
  formatPercent,
  formatHours,
  formatInt,
  formatVariance,
} from '../lib/format.js';
import {
  writableAnnual,
  yearEndEvValues,
  evAxisDomain,
} from '../lib/financials-derive.js';
import { CHART_COLORS } from '../lib/chart-colors.js';
import { CollapsibleSection } from '../components/CollapsibleSection.js';
import { cn } from '../lib/utils.js';
import { addMonthsIso } from '../lib/hours-range.js';

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

function isoFirstOfMonth(year: number, monthIndex: number): string {
  const mm = String(monthIndex + 1).padStart(2, '0');
  return `${year}-${mm}-01`;
}

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

// 'Jun \'25' for the rolling-window axis + label.
function monthShortLabel(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  return `${SHORT_MONTHS[m - 1]} '${String(y).slice(2)}`;
}

// A month "counts" for the rolling chart only if it has one of the two
// charted metrics — NOT just "any KPI" (else the default window could anchor
// on a month where only an unrelated KPI was entered).
function hasChartedMetric(cell: Record<string, number | null> | undefined): boolean {
  return (
    !!cell &&
    (typeof cell['revenue_cents'] === 'number' ||
      typeof cell['monthly_net_profit_cents'] === 'number')
  );
}

// ISO 'YYYY-MM-01' strings sort lexicographically, so plain string compare clamps.
function clampIso(iso: string, lo: string, hi: string): string {
  return iso < lo ? lo : iso > hi ? hi : iso;
}

type TrendView = 'monthly' | 'cumulative' | 'rolling12';

interface RollingProps {
  mergedMonthly: Record<string, Record<string, number | null>>;
  windowEndIso: string;
  annualByYear: Record<number, Record<string, number | null>>;
  onShift: (delta: number) => void;
  canPrev: boolean;
  canNext: boolean;
}

// ============================================================
// Top-level page
// ============================================================
export function FinancialsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const now = new Date();
  // We only operate in 2025–2027; clamp any stale/explicit ?year= into range so
  // the selected year is always present in the dropdown.
  const rawYear = Number(searchParams.get('year')) || now.getFullYear();
  const initialYear = Math.min(2027, Math.max(2025, rawYear));
  const initialMonth = Number(searchParams.get('month')) || now.getMonth() + 1;

  const [year, setYear] = useState(initialYear);
  const [monthIndex, setMonthIndex] = useState(initialMonth - 1);
  const [trendView, setTrendView] = useState<TrendView>(() => {
    const t = searchParams.get('trend');
    return t === 'cumulative' || t === 'rolling12' ? t : 'monthly';
  });
  // Rolling-12mo window end ('YYYY-MM-01'); null → derive default from data.
  const [windowEndIso, setWindowEndIso] = useState<string | null>(() => {
    const w = searchParams.get('win');
    return w && /^\d{4}-\d{2}$/.test(w) ? `${w}-01` : null;
  });

  // Mirror state into the URL so refresh preserves view + window.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('year', String(year));
    next.set('month', String(monthIndex + 1));
    if (trendView === 'monthly') next.delete('trend');
    else next.set('trend', trendView);
    if (trendView === 'rolling12' && windowEndIso) next.set('win', windowEndIso.slice(0, 7));
    else next.delete('win');
    setSearchParams(next, { replace: true });
  }, [year, monthIndex, trendView, windowEndIso, searchParams, setSearchParams]);

  const { data, isPending, error } = useQuery({
    queryKey: qk.financials.year(year),
    queryFn: () => getFinancials(year),
  });
  // Rolling view spans two calendar years → also fetch the prior year (bounded
  // 24-month span). Cheap + cached; lets the rolling tab toggle instantly.
  const priorQuery = useQuery({
    queryKey: qk.financials.year(year - 1),
    queryFn: () => getFinancials(year - 1),
  });

  // Merge both years' monthly cells; keep each year's annual block (targets).
  const mergedMonthly = useMemo(() => {
    const out: Record<string, Record<string, number | null>> = {};
    if (priorQuery.data) Object.assign(out, priorQuery.data.monthly_by_date);
    if (data) Object.assign(out, data.monthly_by_date);
    return out;
  }, [data, priorQuery.data]);
  const annualByYear = useMemo(() => {
    const out: Record<number, Record<string, number | null>> = {};
    if (priorQuery.data) out[year - 1] = priorQuery.data.annual;
    if (data) out[year] = data.annual;
    return out;
  }, [data, priorQuery.data, year]);

  // Window clamps to the fetched 24-month span: end ∈ [Dec(year-1) .. Dec(year)]
  // so the 12-month window start never precedes Jan(year-1). Avoids clamping
  // against not-yet-fetched data.
  const minWindowEnd = `${year - 1}-12-01`;
  const maxWindowEnd = `${year}-12-01`;
  const latestDataMonth = useMemo(() => {
    const months = Object.keys(mergedMonthly)
      .filter((k) => hasChartedMetric(mergedMonthly[k]))
      .sort();
    return months[months.length - 1] ?? null;
  }, [mergedMonthly]);
  const effectiveWindowEnd = clampIso(
    windowEndIso ?? latestDataMonth ?? maxWindowEnd,
    minWindowEnd,
    maxWindowEnd,
  );
  const canPrev = effectiveWindowEnd > minWindowEnd;
  const canNext = effectiveWindowEnd < maxWindowEnd;
  const shiftWindow = (delta: number) =>
    setWindowEndIso(clampIso(addMonthsIso(effectiveWindowEnd, delta), minWindowEnd, maxWindowEnd));

  const monthIso = isoFirstOfMonth(year, monthIndex);
  const monthLabel = MONTH_LABELS[monthIndex] ?? '';

  // Available years: fixed to the years we operate in (2025–2027).
  const availableYears = useMemo(() => [2025, 2026, 2027], []);

  return (
    <div className="pt-3 space-y-8 max-w-7xl mx-auto">
      {/* Header — page title removed; tab nav already identifies the page. */}
      <header className="flex items-center justify-end gap-4">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Year</span>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Month</span>
            <select
              value={monthIndex}
              onChange={(e) => setMonthIndex(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {MONTH_LABELS.map((label, idx) => (
                <option key={label} value={idx}>{label}</option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {isPending && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}
      {error && (
        <div className="text-sm text-destructive">
          Failed to load financials: {error instanceof Error ? error.message : 'unknown error'}
        </div>
      )}

      {data && (
        <>
          {/* Hero tiles — always visible, outside the collapsibles. */}
          <HeroTilesRow
            year={year}
            data={data}
            view={trendView}
            rolling={
              priorQuery.isPending
                ? undefined
                : { mergedMonthly, windowEndIso: effectiveWindowEnd, annualByYear }
            }
          />

          {/* Section 1: Annual Goals — collapsible. Annual table on the left,
              Monthly Trend chart on the right. */}
          <CollapsibleSection title={`Annual Goals — ${year}`} defaultOpen>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AnnualGoalsTable year={year} data={data} />
              <MonthlyTrendChart
                year={year}
                data={data}
                view={trendView}
                onViewChange={setTrendView}
                rolling={
                  priorQuery.isPending
                    ? undefined
                    : {
                        mergedMonthly,
                        windowEndIso: effectiveWindowEnd,
                        annualByYear,
                        onShift: shiftWindow,
                        canPrev,
                        canNext,
                      }
                }
              />
            </div>
          </CollapsibleSection>

          {/* Section 2: Monthly KPIs — collapsible; 4 tables in a 2-col grid. */}
          <CollapsibleSection title={`Monthly KPIs — ${monthLabel} ${year}`} defaultOpen>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <MonthlyKpiTable
                title="Financial"
                table={2}
                showVariance
                year={year}
                monthIso={monthIso}
                data={data}
              />
              <MonthlyKpiTable
                title="Operational"
                table={3}
                showVariance={false}
                year={year}
                monthIso={monthIso}
                data={data}
              />
              <MonthlyKpiTable
                title="Owner Time"
                table={4}
                showVariance={false}
                year={year}
                monthIso={monthIso}
                data={data}
              />
              <MonthlyKpiTable
                title="Sales"
                table={5}
                showVariance={false}
                year={year}
                monthIso={monthIso}
                data={data}
              />
            </div>
          </CollapsibleSection>

        </>
      )}
    </div>
  );
}

// ============================================================
// Hero tiles — top of page
//
// Bronze left-bar variant: white card, 4px accent-color left border (bronze
// neutral, or status colors for off-track tiles), subtle icon top-right,
// trend badge in the bottom-right corner. Hover lifts the tile.
// ============================================================
type TrendDirection = 'up' | 'down' | null;
interface TrendBadge {
  direction: TrendDirection;
  label: string; // pre-formatted; format depends on KPI unit
}

// Trend = projected (EOY estimate) vs target, falling back to current if no
// projection. Format depends on unit:
//   - 'cents' / 'count' / 'hours': relative % delta — "▲ 8.2%"
//   - 'pct': absolute delta in basis points — "▲ 380 bps"
//     (1 percentage point = 100 bps, so a 3.8pp lead = 380 bps)
function computeTrendBadge(
  projected: number | null | undefined,
  current: number | null | undefined,
  target: number | null | undefined,
  unit: FinancialKpiUnit,
): TrendBadge | null {
  if (target === null || target === undefined) return null;
  const actual = projected ?? current;
  if (actual === null || actual === undefined) return null;

  if (unit === 'pct') {
    // Delta in percentage points × 100 = basis points.
    const deltaBps = (actual - target) * 100;
    if (Math.abs(deltaBps) < 1) return { direction: null, label: '0 bps' };
    return {
      direction: deltaBps > 0 ? 'up' : 'down',
      label: `${Math.abs(Math.round(deltaBps))} bps`,
    };
  }

  // Dollar / count / hours units: relative percent above/below target.
  if (target === 0) return null;
  const deltaPct = ((actual - target) / target) * 100;
  if (Math.abs(deltaPct) < 0.05) return { direction: null, label: '0.0%' };
  return {
    direction: deltaPct > 0 ? 'up' : 'down',
    label: `${Math.abs(deltaPct).toFixed(1)}%`,
  };
}

function HeroTile({ label, value, unit, subtext, Icon, trend }: {
  label: string;
  value: number | null;
  unit: FinancialKpiUnit;
  subtext: string;
  Icon: LucideIcon;
  trend?: TrendBadge | null;
}) {
  // Left-bar color encodes status when we have a trend:
  //   up = success (green), down = destructive (red), neutral = bronze.
  const barClass =
    trend?.direction === 'up'
      ? 'border-l-[hsl(145_38%_38%)]'
      : trend?.direction === 'down'
        ? 'border-l-[hsl(0_67%_45%)]'
        : 'border-l-[hsl(36_36%_39%)]';

  return (
    <div
      className={cn(
        'group relative rounded-lg bg-card border border-border border-l-4 p-5 pb-4 overflow-hidden',
        'shadow-[0_1px_2px_rgba(27,42,63,0.05)]',
        'transition-[transform,box-shadow] duration-200',
        'hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(27,42,63,0.12)]',
        barClass,
      )}
    >
      <Icon className="absolute top-4 right-4 h-5 w-5 text-taupe/60" />
      <div className="text-[11px] uppercase tracking-wider text-taupe font-semibold">
        {label}
      </div>
      <div className="text-3xl font-display font-semibold mt-2 tabular-nums text-foreground">
        {value === null ? '—' : displayValue(value, unit)}
      </div>
      <div className="mt-1 flex items-end justify-between gap-2">
        <div className="text-[11px] text-muted-foreground">{subtext}</div>
        {trend && trend.direction !== null && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded text-[11px] font-semibold px-1.5 py-0.5',
              trend.direction === 'up'
                ? 'bg-[hsl(145_38%_92%)] text-[hsl(145_38%_28%)]'
                : 'bg-[hsl(38_92%_92%)] text-[hsl(38_92%_30%)]',
            )}
            aria-label={`${trend.direction === 'up' ? 'Above' : 'Below'} target by ${trend.label}`}
          >
            <span aria-hidden>{trend.direction === 'up' ? '▲' : '▼'}</span>
            {trend.label}
          </span>
        )}
      </div>
    </div>
  );
}

function HeroTilesRow({ year, data, view, rolling }: {
  year: number;
  data: FinancialsResponseClient;
  view: TrendView;
  rolling?: {
    mergedMonthly: Record<string, Record<string, number | null>>;
    windowEndIso: string;
    annualByYear: Record<number, Record<string, number | null>>;
  };
}) {
  // Default = YTD headline from the ANNUAL JSONB (server-derived sums for the
  // selected year), with a projected-vs-target trend badge. Net Margin is
  // server-derived (net_profit / annual_revenue * 100).
  let revenue: number | null = data.annual['annual_revenue_cents'] ?? null;
  let netProfit: number | null = data.annual['net_profit_cents'] ?? null;
  let margin: number | null = data.annual['net_margin_pct'] ?? null;
  const inventoryValue = data.annual['inventory_value_cents'] ?? 0;

  let revenueSub = `${year} current`;
  let profitSub = `${year} EBITDA`;
  let marginSub = 'Auto-computed';

  let revenueTrend = computeTrendBadge(
    data.annual['annual_revenue_cents_projected'] as number | null | undefined, revenue,
    data.annual['annual_revenue_cents_target'] as number | null | undefined, 'cents',
  );
  let profitTrend = computeTrendBadge(
    data.annual['net_profit_cents_projected'] as number | null | undefined, netProfit,
    data.annual['net_profit_cents_target'] as number | null | undefined, 'cents',
  );
  let marginTrend = computeTrendBadge(
    data.annual['net_margin_pct_projected'] as number | null | undefined, margin,
    data.annual['net_margin_pct_target'] as number | null | undefined, 'pct',
  );

  // Rolling 12-mo tab: the three money cards show the trailing-12-month
  // actuals for the selected window, compared actual-vs-annual-target (no
  // projection — a full 12 months IS the actual). A metric with <12 months of
  // data in the window is tagged so the understated figure isn't misread.
  if (view === 'rolling12' && rolling) {
    const { mergedMonthly, windowEndIso, annualByYear } = rolling;
    const sumWin = (key: string) => {
      let s = 0, n = 0;
      for (let i = 0; i < 12; i++) {
        const v = mergedMonthly[addMonthsIso(windowEndIso, -i)]?.[key];
        if (typeof v === 'number') { s += v; n++; }
      }
      return { s, n };
    };
    const rev = sumWin('revenue_cents');
    const net = sumWin('monthly_net_profit_cents');
    revenue = rev.n > 0 ? rev.s : null;
    netProfit = net.n > 0 ? net.s : null;
    margin = rev.s > 0 && net.n > 0 ? (net.s / rev.s) * 100 : null;

    const endYear = Number(windowEndIso.slice(0, 4));
    const ann = annualByYear[endYear] ?? {};
    const winLabel = `${monthShortLabel(addMonthsIso(windowEndIso, -11))} – ${monthShortLabel(windowEndIso)}`;
    const tag = (n: number) => (n < 12 ? ` · ${n}/12 mo` : '');
    revenueSub = `${winLabel}${tag(rev.n)}`;
    profitSub = `${winLabel}${tag(net.n)}`;
    marginSub = `${winLabel}${tag(Math.min(rev.n, net.n))}`;

    revenueTrend = computeTrendBadge(null, revenue, ann['annual_revenue_cents_target'], 'cents');
    profitTrend = computeTrendBadge(null, netProfit, ann['net_profit_cents_target'], 'cents');
    marginTrend = computeTrendBadge(null, margin, ann['net_margin_pct_target'], 'pct');
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <HeroTile label="Annual Revenue"  value={revenue}        unit="cents" subtext={revenueSub}     Icon={DollarSign} trend={revenueTrend} />
      <HeroTile label="Net Profit"      value={netProfit}      unit="cents" subtext={profitSub}      Icon={TrendingUp} trend={profitTrend} />
      <HeroTile label="Net Margin %"    value={margin}         unit="pct"   subtext={marginSub}      Icon={Percent}    trend={marginTrend} />
      <HeroTile label="Inventory Value" value={inventoryValue} unit="cents" subtext="Auto-computed"  Icon={Package} />
    </div>
  );
}

// ============================================================
// Table 1 — Annual Goals
// Cols: Metric | Current | Target | Status
// ============================================================
function AnnualGoalsTable({ year, data }: { year: number; data: FinancialsResponseClient }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
            <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">Metric</th>
            <th className="text-right px-4 py-2 font-semibold w-32">YTD</th>
            <th className="text-right px-4 py-2 font-semibold w-32">Projected</th>
            <th className="text-right px-4 py-2 font-semibold w-32">Target</th>
            <th className="text-center px-4 py-2 font-semibold w-32">Status</th>
          </tr>
        </thead>
        <tbody>
          {ANNUAL_KPIS.map((kpi) => {
            const currentKey = kpi.key;
            const targetKey = `${kpi.key}_target`;
            const projectedKey = `${kpi.key}_projected`;
            const current = data.annual[currentKey] ?? null;
            const target = data.annual[targetKey] ?? null;
            const projected = data.annual[projectedKey] ?? null;
            // Status compares Projected vs Target when a projection exists
            // (mid-year forecast view); falls back to Current vs Target
            // otherwise (rows that don't project, e.g. Owner Hours).
            const statusBasis = projected ?? current;
            return (
              <tr key={kpi.key} className="border-t border-border/40">
                <td className="px-4 py-3 font-medium whitespace-nowrap">{kpi.label}</td>
                <td className="text-right px-4 py-3">
                  {kpi.source === 'derived' ? (
                    <span
                      className="tabular-nums cursor-default"
                      title="Auto-computed (not editable). Source depends on KPI — Inventory Value rolls up active SKUs; Annual Revenue / Net Profit roll up monthly entries; Net Margin = profit ÷ revenue; Estimated Value = 4× Net Profit."
                    >
                      {current === null ? '—' : displayValue(current, kpi.unit)}
                    </span>
                  ) : (
                    <AnnualCellInput
                      year={year}
                      fieldKey={currentKey}
                      unit={kpi.unit}
                      value={current}
                      data={data}
                    />
                  )}
                </td>
                <td className="text-right px-4 py-3 italic text-muted-foreground">
                  {projected === null ? (
                    <span className="not-italic">—</span>
                  ) : (
                    <span
                      className="tabular-nums cursor-default"
                      title="12-month projection: (sum of months entered ÷ months entered) × 12."
                    >
                      {displayValue(projected, kpi.unit)}
                    </span>
                  )}
                </td>
                <td className="text-right px-4 py-3">
                  {kpi.targetSource === 'derived' ? (
                    <span
                      className="tabular-nums cursor-default"
                      title="Auto-computed from related target KPIs (e.g. Net Profit target / Annual Revenue target)."
                    >
                      {target === null ? '—' : displayValue(target, kpi.unit)}
                    </span>
                  ) : (
                    <AnnualCellInput
                      year={year}
                      fieldKey={targetKey}
                      unit={kpi.unit}
                      value={target}
                      data={data}
                    />
                  )}
                </td>
                <td className="text-center px-4 py-3">
                  <HarveyBall current={statusBasis} target={target} direction={kpi.direction} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Tables 2-5 — Monthly KPIs
// Cols: KPI | This Month | Target | (Variance, only for table 2)
// ============================================================
function MonthlyKpiTable({
  title,
  table,
  showVariance,
  year,
  monthIso,
  data,
}: {
  title: string;
  table: 2 | 3 | 4 | 5;
  showVariance: boolean;
  year: number;
  monthIso: string;
  data: FinancialsResponseClient;
}) {
  const kpis = MONTHLY_KPIS.filter((k) => k.table === table);
  const monthData = data.monthly_by_date[monthIso] ?? {};

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="px-4 py-2 border-b border-border bg-secondary/30">
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
            <th className="text-left px-4 py-2 font-semibold">KPI</th>
            <th className="text-right px-4 py-2 font-semibold w-32">This Month</th>
            <th className="text-right px-4 py-2 font-semibold w-32">Target</th>
            {showVariance && (
              <th className="text-right px-4 py-2 font-semibold w-32">Variance</th>
            )}
          </tr>
        </thead>
        <tbody>
          {kpis.map((kpi) => {
            const actual = monthData[kpi.key] ?? null;
            const target = data.monthly_targets[kpi.key] ?? null;
            const variance =
              actual !== null && target !== null
                ? signedVariance(actual, target, kpi.direction)
                : null;
            const tintClass =
              variance === null
                ? ''
                : variance > 0
                  ? 'bg-emerald-50/50'
                  : variance < 0
                    ? 'bg-rose-50/50'
                    : '';
            return (
              <tr key={kpi.key} className={cn('border-t border-border/40', tintClass)}>
                <td className="px-4 py-3 font-medium">{kpi.label}</td>
                <td className="text-right px-4 py-3">
                  <MonthlyCellInput
                    monthIso={monthIso}
                    year={year}
                    fieldKey={kpi.key}
                    unit={kpi.unit}
                    value={actual}
                    data={data}
                  />
                </td>
                <td className="text-right px-4 py-3">
                  <MonthlyTargetCellInput
                    year={year}
                    fieldKey={kpi.key}
                    unit={kpi.unit}
                    value={target}
                    data={data}
                  />
                </td>
                {showVariance && (
                  <td
                    className={cn(
                      'text-right px-4 py-3 font-medium',
                      variance === null
                        ? 'text-muted-foreground'
                        : variance > 0
                          ? 'text-emerald-700'
                          : variance < 0
                            ? 'text-rose-700'
                            : 'text-muted-foreground',
                    )}
                  >
                    {variance === null ? '—' : formatVariance(variance, kpi.unit)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Trend chart — toggles between Monthly (per-month) and Cumulative
// (running sums + EV projection fan on the right Y-axis).
//
// Monthly view: Gross = revenue_cents, Net = monthly_net_profit_cents.
// Missing months → null → gap (connectNulls={false}).
//
// Cumulative view: cumGross + cumNet are running sums. Three dotted EV
// rays anchor at the last month with actual net profit and project to
// December at 3.5× / 4.0× (flat baseline) / 4.5× multiples on a separate
// right-side Y-axis. View state is owned by FinancialsPage.
// ============================================================
function MonthlyTrendChart({
  year,
  data,
  view,
  onViewChange,
  rolling,
}: {
  year: number;
  data: FinancialsResponseClient;
  view: TrendView;
  onViewChange: (next: TrendView) => void;
  rolling?: RollingProps;
}) {
  const isCumulative = view === 'cumulative';
  const isRolling = view === 'rolling12';
  const netProfitProjected = (data.annual['net_profit_cents_projected'] as number | null | undefined) ?? null;

  interface TrendChartRow {
    month: string;
    gross: number | null;
    net: number | null;
    cumGross: number | null;
    cumNet: number | null;
    ev_3_5: number | null;
    ev_4_0: number | null;
    ev_4_5: number | null;
  }

  // Pre-pass: compute cumulative running sums AND track the last month with
  // numeric data for each series. Needed up-front (not inside the data map)
  // so we can pin both YAxis domains explicitly before mapping the EV
  // anchor point — the right-axis equivalent of "where the Net line ends"
  // requires both domains to be known.
  let cumGrossSum = 0;
  let cumNetSum = 0;
  let lastGrossIdx = -1;
  let lastNetIdx = -1;
  const cumulativeRows = MONTH_LABELS.map((_, idx) => {
    const monthIso = isoFirstOfMonth(year, idx);
    const m = data.monthly_by_date[monthIso];
    const monthGross = m?.['revenue_cents'];
    const monthNet = m?.['monthly_net_profit_cents'];
    if (typeof monthGross === 'number') { cumGrossSum += monthGross; lastGrossIdx = idx; }
    if (typeof monthNet === 'number')   { cumNetSum   += monthNet;   lastNetIdx   = idx; }
    return {
      cumGross: idx <= lastGrossIdx ? cumGrossSum : null,
      cumNet:   idx <= lastNetIdx   ? cumNetSum   : null,
    };
  });

  // Left Y-axis domain: pinned to [0, 110% of the larger of the two final
  // cumulative values]. Pinning is required so the EV-anchor pixel mapping
  // (below) lines up exactly with where the solid Net line ends on screen.
  const lastNetValue = lastNetIdx >= 0 ? cumulativeRows[lastNetIdx]!.cumNet : null;
  const lastGrossValue = lastGrossIdx >= 0 ? cumulativeRows[lastGrossIdx]!.cumGross : null;
  const leftMaxRaw = Math.max(lastGrossValue ?? 0, lastNetValue ?? 0);
  const leftDomainMax = leftMaxRaw > 0 ? Math.ceil(leftMaxRaw * 1.1) : 1;
  const leftDomain: [number, number] = [0, leftDomainMax];

  // Right Y-axis domain: 25% below worst-case (3.5×), 25% above best-case
  // (4.5×). Pinned so the three EV endpoints fill the middle of the axis
  // instead of clustering near the top of an auto-scaled [0, ev_4_5*1.1]
  // domain.
  const rightDomain = evAxisDomain(netProfitProjected);

  // Show the EV layer (right axis + three projection lines) only when:
  //  - We're in cumulative view
  //  - There's a projection to anchor against (NPP not null)
  //  - There's at least one actual net-profit data point (so we have an
  //    end-of-Net-line anchor)
  const showEvAxis =
    isCumulative && rightDomain !== null && lastNetIdx >= 0 && lastNetValue !== null;

  // Map the "end of Net line" pixel from the LEFT axis to its equivalent
  // value on the RIGHT axis, so the three EV lines visually emerge from
  // exactly where Net stops (regardless of the two axes' different ranges).
  // Same fractional height on both axes → same screen pixel.
  let evAnchorRightY: number | null = null;
  if (showEvAxis && rightDomain) {
    const fraction = Math.max(0, Math.min(1, lastNetValue! / leftDomainMax));
    evAnchorRightY = rightDomain[0] + (rightDomain[1] - rightDomain[0]) * fraction;
  }

  const yearEndEv = yearEndEvValues(netProfitProjected);

  // Linearly interpolate the three EV values across rows from anchor to Dec.
  // Two reasons we populate every intermediate row (not just anchor + Dec):
  //  1. Recharts with connectNulls=true CAN draw across null gaps, but the
  //     tooltip at intermediate X-positions only sees what's in chartData.
  //     If those rows are null, the tooltip won't list EV values when
  //     hovering at e.g. August.
  //  2. The interpolated value is replaced in the tooltip formatter with the
  //     year-end value — so the rendered value at intermediate X is geometry
  //     only; user always sees the final EV regardless of hover position.
  function interpEv(idx: number, endY: number | null): number | null {
    if (!showEvAxis || evAnchorRightY === null || endY === null) return null;
    if (idx < lastNetIdx) return null;
    if (idx === lastNetIdx) return evAnchorRightY;
    if (idx === 11) return endY;
    const progress = (idx - lastNetIdx) / (11 - lastNetIdx);
    return evAnchorRightY + (endY - evAnchorRightY) * progress;
  }

  const chartData: TrendChartRow[] = isCumulative
    ? MONTH_LABELS.map((label, idx) => {
        const row = cumulativeRows[idx]!;
        return {
          month: label.slice(0, 3),
          gross: null,
          net: null,
          cumGross: row.cumGross,
          cumNet: row.cumNet,
          ev_3_5: interpEv(idx, yearEndEv.ev_3_5),
          ev_4_0: interpEv(idx, yearEndEv.ev_4_0),
          ev_4_5: interpEv(idx, yearEndEv.ev_4_5),
        };
      })
    : MONTH_LABELS.map((label, idx) => {
        const monthIso = isoFirstOfMonth(year, idx);
        const m = data.monthly_by_date[monthIso];
        return {
          month: label.slice(0, 3),
          gross: m?.['revenue_cents'] ?? null,
          net: m?.['monthly_net_profit_cents'] ?? null,
          cumGross: null,
          cumNet: null,
          ev_3_5: null,
          ev_4_0: null,
          ev_4_5: null,
        };
      });

  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3 gap-3">
        <h3 className="text-sm font-semibold">
          {isRolling ? 'Rolling 12-Month' : isCumulative ? 'Cumulative Trend' : 'Monthly Trend'}
          {!isRolling && ` — ${year}`}
        </h3>
        {/* Segmented toggle — house pattern (active: border-primary bg-accent
            text-primary). Three views: monthly / cumulative / rolling-12mo. */}
        <div className="grid grid-cols-3 gap-1.5">
          {(['monthly', 'cumulative', 'rolling12'] as const).map((v) => {
            const active = view === v;
            const label = v === 'rolling12' ? 'Rolling 12-mo' : v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => onViewChange(v)}
                className={cn(
                  'flex h-7 items-center justify-center px-3 rounded-md border text-xs font-medium capitalize whitespace-nowrap transition-colors',
                  active
                    ? 'border-primary bg-accent text-primary'
                    : 'border-border bg-card text-muted-foreground hover:bg-accent/40',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {/* flex-1 + min-h-0 lets the chart consume all remaining vertical space
          in the card (which the parent 2-col grid stretches to match
          AnnualGoalsTable's height). min-h-[260px] keeps a sane floor when
          the row is short. */}
      <div className="flex-1 min-h-[260px]">
        {isRolling ? (
          rolling ? (
            <RollingChartBody {...rolling} />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
          )
        ) : (
        <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <XAxis dataKey="month" fontSize={11} stroke={CHART_COLORS.axis} />
          <YAxis
            domain={isCumulative ? leftDomain : undefined}
            tickFormatter={(v: number) => v == null ? '' : formatCentsK(v)}
            fontSize={11}
            stroke={CHART_COLORS.axis}
            width={56}
          />
          {showEvAxis && rightDomain && (
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={rightDomain}
              tickFormatter={(v: number) => v == null ? '' : formatCentsK(v)}
              fontSize={11}
              stroke={CHART_COLORS.axis}
              width={64}
              label={{
                value: 'Enterprise Value',
                angle: -90,
                position: 'insideRight',
                style: { textAnchor: 'middle', fontSize: 11, fill: CHART_COLORS.axis },
              }}
            />
          )}
          <Tooltip
            content={(props) => {
              const { active, payload, label } = props;
              if (!active || !payload || payload.length === 0) return null;

              if (!isCumulative) {
                // Monthly view: standard month-header + each non-null series.
                return (
                  <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-md">
                    <div className="font-medium mb-1">{String(label ?? '')}</div>
                    {payload.map((entry) => {
                      if (entry.value == null) return null;
                      return (
                        <div key={String(entry.dataKey)} style={{ color: String(entry.color ?? '') }}>
                          {String(entry.name)}: {formatCentsRounded(Number(entry.value))}
                        </div>
                      );
                    })}
                  </div>
                );
              }

              // Cumulative view: no month header.
              // Gross / Net shown only when their value exists at the
              // hovered X. EV section appears ONLY when the cursor is
              // past the actual data (neither Gross nor Net has a value
              // at this X) — i.e., the user has moved into the projection
              // region where only the dashed lines exist.
              const grossEntry = payload.find((p) => p.name === 'Gross');
              const netEntry = payload.find((p) => p.name === 'Net');
              const grossValue = grossEntry?.value;
              const netValue = netEntry?.value;
              const hasGross = typeof grossValue === 'number';
              const hasNet = typeof netValue === 'number';
              const hasActualHere = hasGross || hasNet;
              const showEvSection = yearEndEv.ev_4_0 !== null && !hasActualHere;

              if (!hasActualHere && !showEvSection) return null;

              return (
                <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-md min-w-[140px] space-y-1.5">
                  {hasActualHere && (
                    <div className="font-medium">{String(label ?? '')}</div>
                  )}
                  {hasGross && (
                    <div style={{ color: CHART_COLORS.gross }}>
                      Gross: {formatCentsRounded(grossValue as number)}
                    </div>
                  )}
                  {hasNet && (
                    <div style={{ color: CHART_COLORS.net }}>
                      Net: {formatCentsRounded(netValue as number)}
                    </div>
                  )}
                  {showEvSection && (
                    <>
                      <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
                        Year-end EV
                      </div>
                      <div>4.5×: {formatCentsRounded(yearEndEv.ev_4_5!)}</div>
                      <div>4.0×: {formatCentsRounded(yearEndEv.ev_4_0!)}</div>
                      <div>3.5×: {formatCentsRounded(yearEndEv.ev_3_5!)}</div>
                    </>
                  )}
                </div>
              );
            }}
          />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
          {isCumulative ? (
            <>
              <Line type="monotone" dataKey="cumGross" name="Gross" stroke={CHART_COLORS.gross} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
              <Line type="monotone" dataKey="cumNet"   name="Net"   stroke={CHART_COLORS.net}   strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
              {showEvAxis && (
                <>
                  {/* EV projection lines: three visually identical thin,
                      wide-dash, faint segments from the anchor (where Net
                      ends, mapped to the right-axis pixel) to Dec at the
                      three multiples. Custom Tooltip surfaces the
                      year-end EV only when hovered past the actual data;
                      legend is suppressed via legendType=none. */}
                  <Line yAxisId="right" type="linear" dataKey="ev_3_5" name="EV 3.5×" stroke={CHART_COLORS.axis} strokeOpacity={0.5} strokeWidth={1} strokeDasharray="8 5" dot={false} connectNulls legendType="none" />
                  <Line yAxisId="right" type="linear" dataKey="ev_4_0" name="EV 4.0×" stroke={CHART_COLORS.axis} strokeOpacity={0.5} strokeWidth={1} strokeDasharray="8 5" dot={false} connectNulls legendType="none" />
                  <Line yAxisId="right" type="linear" dataKey="ev_4_5" name="EV 4.5×" stroke={CHART_COLORS.axis} strokeOpacity={0.5} strokeWidth={1} strokeDasharray="8 5" dot={false} connectNulls legendType="none" />
                </>
              )}
            </>
          ) : (
            <>
              <Line type="monotone" dataKey="gross" name="Gross" stroke={CHART_COLORS.gross} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
              <Line type="monotone" dataKey="net"   name="Net"   stroke={CHART_COLORS.net}   strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
            </>
          )}
        </LineChart>
        </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Rolling 12-month (TTM) chart body — rendered inside MonthlyTrendChart's
// body when view==='rolling12'. Cumulative build of ACTUAL revenue + net
// profit across a slidable 12-month window, vs the window-end year's ANNUAL
// target (flat lines). Partial windows (<12 months with data) are badged and
// drawn muted/dashed with the target suppressed — a partial cumulative is NOT
// a TTM and must not read as a target verdict.
// ============================================================
interface RollRow {
  month: string;
  cumGross: number | null;
  cumNet: number | null;
  revTarget: number | null;
  netTarget: number | null;
}

function RollingChartBody({
  mergedMonthly,
  windowEndIso,
  annualByYear,
  onShift,
  canPrev,
  canNext,
}: RollingProps) {
  const windowMonths = Array.from({ length: 12 }, (_, i) =>
    addMonthsIso(windowEndIso, -(11 - i)),
  );
  const windowStartIso = windowMonths[0]!;

  let cumGross = 0;
  let cumNet = 0;
  let lastGrossIdx = -1;
  let lastNetIdx = -1;
  let monthsWithData = 0;

  const endYear = Number(windowEndIso.slice(0, 4));
  const annual = annualByYear[endYear] ?? {};
  const revTargetRaw = annual['annual_revenue_cents_target'];
  const netTargetRaw = annual['net_profit_cents_target'];
  const revTarget = typeof revTargetRaw === 'number' ? revTargetRaw : null;
  const netTarget = typeof netTargetRaw === 'number' ? netTargetRaw : null;

  const baseRows = windowMonths.map((iso, idx) => {
    const m = mergedMonthly[iso];
    const g = m?.['revenue_cents'];
    const n = m?.['monthly_net_profit_cents'];
    if (typeof g === 'number') { cumGross += g; lastGrossIdx = idx; }
    if (typeof n === 'number') { cumNet += n; lastNetIdx = idx; }
    if (hasChartedMetric(m)) monthsWithData++;
    return {
      iso,
      cumGross: idx <= lastGrossIdx ? cumGross : null,
      cumNet: idx <= lastNetIdx ? cumNet : null,
    };
  });

  const complete = monthsWithData === 12;

  // Targets only participate in the verdict (and the y-domain) on a full
  // window — otherwise a partial cumulative endpoint would read as a miss.
  const showTargets = complete;
  const rows: RollRow[] = baseRows.map((r) => ({
    month: monthShortLabel(r.iso),
    cumGross: r.cumGross,
    cumNet: r.cumNet,
    revTarget: showTargets ? revTarget : null,
    netTarget: showTargets ? netTarget : null,
  }));

  const endGross = lastGrossIdx >= 0 ? baseRows[lastGrossIdx]!.cumGross ?? 0 : 0;
  const endNet = lastNetIdx >= 0 ? baseRows[lastNetIdx]!.cumNet ?? 0 : 0;
  const maxVal = Math.max(endGross, endNet, showTargets ? (revTarget ?? 0) : 0, showTargets ? (netTarget ?? 0) : 0);
  const domainMax = maxVal > 0 ? Math.ceil(maxVal * 1.1) : 1;

  const windowLabel = `${monthShortLabel(windowStartIso)} – ${monthShortLabel(windowEndIso)}`;
  const badge = complete ? '12 / 12 ✓' : `${monthsWithData} / 12`;

  const stepBtn =
    'flex h-7 w-7 items-center justify-center rounded-md border text-xs transition-colors ' +
    'border-border bg-card text-muted-foreground enabled:hover:bg-accent/40 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          <button type="button" className={stepBtn} disabled={!canPrev} onClick={() => onShift(-1)} aria-label="Earlier 12 months">‹</button>
          <span className="text-xs font-medium tabular-nums min-w-[120px] text-center">{windowLabel}</span>
          <button type="button" className={stepBtn} disabled={!canNext} onClick={() => onShift(1)} aria-label="Later 12 months">›</button>
        </div>
        <span
          className={cn(
            'text-[10px] font-medium px-1.5 py-0.5 rounded border',
            complete
              ? 'border-[hsl(145_38%_38%)] text-[hsl(145_38%_38%)]'
              : 'border-border text-muted-foreground',
          )}
          title={complete ? 'Full 12-month window — comparable to the annual target' : 'Incomplete window — not yet a true 12-month total'}
        >
          {badge}
        </span>
      </div>

      {monthsWithData === 0 ? (
        <div className="flex-1 min-h-[220px] flex items-center justify-center text-sm text-muted-foreground">
          No data in this 12-month window — slide ‹ › to a populated span.
        </div>
      ) : (
        <div className="flex-1 min-h-[220px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <XAxis dataKey="month" fontSize={11} stroke={CHART_COLORS.axis} />
              <YAxis
                domain={[0, domainMax]}
                tickFormatter={(v: number) => (v == null ? '' : formatCentsK(v))}
                fontSize={11}
                stroke={CHART_COLORS.axis}
                width={56}
              />
              <Tooltip
                content={(props) => {
                  const { active, payload, label } = props;
                  if (!active || !payload || payload.length === 0) return null;
                  const g = payload.find((p) => p.name === 'Gross')?.value;
                  const n = payload.find((p) => p.name === 'Net')?.value;
                  return (
                    <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-md min-w-[150px] space-y-1">
                      <div className="font-medium">{String(label ?? '')} (cumulative)</div>
                      {typeof g === 'number' && (
                        <div style={{ color: CHART_COLORS.gross }}>Gross: {formatCentsRounded(g)}</div>
                      )}
                      {typeof n === 'number' && (
                        <div style={{ color: CHART_COLORS.net }}>Net: {formatCentsRounded(n)}</div>
                      )}
                      {showTargets && revTarget !== null && (
                        <div className="text-muted-foreground">Revenue target ({endYear}): {formatCentsRounded(revTarget)}</div>
                      )}
                      {showTargets && netTarget !== null && (
                        <div className="text-muted-foreground">EBITDA target ({endYear}): {formatCentsRounded(netTarget)}</div>
                      )}
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
              <Line type="monotone" dataKey="cumGross" name="Gross" stroke={CHART_COLORS.gross} strokeWidth={2} strokeOpacity={complete ? 1 : 0.55} strokeDasharray={complete ? undefined : '5 4'} dot={{ r: 3 }} connectNulls={false} />
              <Line type="monotone" dataKey="cumNet" name="Net" stroke={CHART_COLORS.net} strokeWidth={2} strokeOpacity={complete ? 1 : 0.55} strokeDasharray={complete ? undefined : '5 4'} dot={{ r: 3 }} connectNulls={false} />
              {showTargets && revTarget !== null && (
                <Line type="linear" dataKey="revTarget" name={`Rev target ${endYear}`} stroke={CHART_COLORS.gross} strokeOpacity={0.5} strokeWidth={1} strokeDasharray="8 5" dot={false} connectNulls legendType="none" />
              )}
              {showTargets && netTarget !== null && (
                <Line type="linear" dataKey="netTarget" name={`Net target ${endYear}`} stroke={CHART_COLORS.net} strokeOpacity={0.5} strokeWidth={1} strokeDasharray="8 5" dot={false} connectNulls legendType="none" />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Harvey ball — Table 1 Status column.
// Compares value vs target (with direction normalization) and renders a
// small colored dot: green = at-or-better, yellow = within 10% under,
// red = >10% under.
// ============================================================
function HarveyBall({
  current,
  target,
  direction,
}: {
  current: number | null;
  target: number | null;
  direction: FinancialKpiDirection;
}) {
  if (current === null || target === null) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  const signed = signedVariance(current, target, direction);
  const absTarget = Math.abs(target);
  let tone: 'green' | 'yellow' | 'red';
  if (signed >= 0) {
    tone = 'green';
  } else if (absTarget === 0 || signed / absTarget >= -0.10) {
    // Within 10% under target (or undefined ratio when target=0 but signed<0)
    tone = 'yellow';
  } else {
    tone = 'red';
  }
  const color = tone === 'green'
    ? 'bg-emerald-500'
    : tone === 'yellow'
      ? 'bg-amber-400'
      : 'bg-rose-500';
  const label = tone === 'green' ? 'On track' : tone === 'yellow' ? 'Close to target' : 'Off track';
  return (
    <span
      className={cn('inline-block w-3 h-3 rounded-full', color)}
      role="status"
      aria-label={label}
      title={label}
    />
  );
}

// ============================================================
// Display formatting per unit
// ============================================================
function displayValue(value: number, unit: FinancialKpiUnit): string {
  switch (unit) {
    case 'cents': return formatCentsRounded(value);
    case 'pct':   return formatPercent(value);
    case 'hours': return formatHours(value);
    case 'int':   return formatInt(value);
  }
}

// ============================================================
// Inline-edit cells
// ============================================================
interface CellInputProps {
  unit: FinancialKpiUnit;
  value: number | null;
  onSave: (next: number | null) => Promise<void>;
}

function CellInput({ unit, value, onSave }: CellInputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  function startEdit() {
    if (saving) return;
    // For cents, show in dollars to the user (they don't type cents).
    if (value === null) {
      setDraft('');
    } else if (unit === 'cents') {
      setDraft(String(value / 100));
    } else {
      setDraft(String(value));
    }
    setIsEditing(true);
  }

  async function commit() {
    setIsEditing(false);
    const trimmed = draft.trim();
    if (trimmed === '') {
      // Empty input clears the cell.
      if (value !== null) {
        setSaving(true);
        try { await onSave(null); } finally { setSaving(false); }
      }
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return;
    const next = unit === 'cents' ? Math.round(parsed * 100) : parsed;
    if (next === value) return;
    setSaving(true);
    try {
      await onSave(next);
    } finally {
      setSaving(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
    }
  }

  if (isEditing) {
    const prefix = unit === 'cents' ? '$' : '';
    const suffix = unit === 'pct' ? '%' : unit === 'hours' ? 'hrs' : '';
    return (
      <span className="inline-flex items-center gap-1">
        {prefix && <span className="text-muted-foreground">{prefix}</span>}
        <input
          ref={inputRef}
          type="number"
          step="any"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={onKeyDown}
          className="w-24 h-7 rounded border border-input bg-background px-2 text-right text-sm"
        />
        {suffix && <span className="text-muted-foreground text-xs">{suffix}</span>}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      disabled={saving}
      className={cn(
        'text-right tabular-nums hover:bg-accent/30 rounded px-1.5 py-0.5 -mr-1.5 transition-colors',
        saving && 'opacity-50',
        value === null && 'text-muted-foreground',
      )}
    >
      {value === null ? '—' : displayValue(value, unit)}
    </button>
  );
}

// ============================================================
// Mutation wrappers — push full JSONB blob back on every edit.
// (Owner edits monthly; round-trip is generous, no optimistic update.)
// ============================================================
function AnnualCellInput({
  year,
  fieldKey,
  unit,
  value,
  data,
}: {
  year: number;
  fieldKey: string;
  unit: FinancialKpiUnit;
  value: number | null;
  data: FinancialsResponseClient;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (next: number | null) => {
      // Strip derived keys (e.g. inventory_value_cents) — server rejects
      // them on write since they're auto-computed.
      const nextAnnual = { ...writableAnnual(data.annual), [fieldKey]: next };
      return putFinancialsAnnual(year, {
        annual: nextAnnual,
        monthly_targets: data.monthly_targets,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.financials.year(year) });
    },
  });
  return (
    <CellInput
      unit={unit}
      value={value}
      onSave={async (next) => {
        await mutation.mutateAsync(next);
      }}
    />
  );
}

function MonthlyTargetCellInput({
  year,
  fieldKey,
  unit,
  value,
  data,
}: {
  year: number;
  fieldKey: string;
  unit: FinancialKpiUnit;
  value: number | null;
  data: FinancialsResponseClient;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (next: number | null) => {
      const nextTargets = { ...data.monthly_targets, [fieldKey]: next };
      return putFinancialsAnnual(year, {
        annual: writableAnnual(data.annual),
        monthly_targets: nextTargets,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.financials.year(year) });
    },
  });
  return (
    <CellInput
      unit={unit}
      value={value}
      onSave={async (next) => {
        await mutation.mutateAsync(next);
      }}
    />
  );
}

function MonthlyCellInput({
  year,
  monthIso,
  fieldKey,
  unit,
  value,
  data,
}: {
  year: number;
  monthIso: string;
  fieldKey: string;
  unit: FinancialKpiUnit;
  value: number | null;
  data: FinancialsResponseClient;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (next: number | null) => {
      const existing = data.monthly_by_date[monthIso] ?? {};
      const nextData = { ...existing, [fieldKey]: next };
      return putFinancialsMonthly(monthIso, { data: nextData });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.financials.year(year) });
    },
  });
  return (
    <CellInput
      unit={unit}
      value={value}
      onSave={async (next) => {
        await mutation.mutateAsync(next);
      }}
    />
  );
}

// Re-export icon for the AppShell nav item so we don't import lucide twice.
export const FinancialsIcon = TrendingUp;

// Silence unused-helper warning for the type re-export.
export type { KpiDef };
